import { MoreHorizontal } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { computeFlowPaths } from "@/components/ui/atoms/compute-flow-paths";
import {
  FlowConnector,
  type FlowConnectorLineStyle,
  type FlowConnectorVariant,
} from "@/components/ui/atoms/flow-connector";
import { FlowTile } from "@/components/ui/atoms/flow-tile";
import { cn } from "@/lib/utils";

const COMPACT_VISIBLE = 3;
const COMPACT_COLUMNS = 2;

const FULL_VISIBLE_LEFT = 5;
const FULL_VISIBLE_RIGHT = 8;
const FULL_COLUMNS = 3;

type FlowAnimateMode = "both" | "left" | "right" | "none";
type FlowSize = "sm" | "md" | "lg";

type FlowDiagramProps = {
  /** Items rendered on the left side, fanning into the hub. */
  left: ReactNode[];
  /** Items rendered on the right side, fanning out from the hub. */
  right: ReactNode[];
  /**
   * Center mark — typically a `FlowHub` wrapping a logo. Rendered as-is.
   */
  center: ReactNode;
  /**
   * Which sides animate. `both` is the default; `none` renders static
   * dashed lines.
   */
  animate?: FlowAnimateMode;
  /**
   * Animation style passed to both `FlowConnector` instances. Defaults
   * to `flow`.
   */
  animation?: FlowConnectorVariant;
  /**
   * Stroke pattern for the connectors. Defaults to `dashed` for the
   * `flow` animation and `dotted` for `dot`.
   */
  lineStyle?: FlowConnectorLineStyle;
  /**
   * Overall scale. Defaults to `md`. `sm` reduces the full-layout
   * connector width slightly; the compact layout already uses smaller
   * dimensions.
   */
  size?: FlowSize;
  /**
   * Override the full-layout visible counts. The compact layout always
   * caps at 3 per side. Pass `{ left: 4 }` to show 4 items on the left
   * at wide container widths instead of the default 5.
   */
  maxVisible?: { left?: number; right?: number };
} & Omit<ComponentProps<"div">, "children">;

/**
 * Hub-and-spoke flow visualisation. Items fan out from a center mark on
 * both sides via animated SVG connectors. Adapts between a compact and
 * a full layout based on container width (Tailwind's `@container`).
 *
 * Use `GatewayDiagram` for the Arcade-branded preset with default MCP
 * client/server icon catalogues.
 */
function FlowDiagram({
  left,
  right,
  center,
  animate = "both",
  animation = "flow",
  lineStyle,
  size = "md",
  maxVisible,
  className,
  "aria-label": ariaLabel = "Flow diagram",
  ...props
}: FlowDiagramProps) {
  const leftFullCap = maxVisible?.left ?? FULL_VISIBLE_LEFT;
  const rightFullCap = maxVisible?.right ?? FULL_VISIBLE_RIGHT;

  const animateLeft = animate === "both" || animate === "left";
  const animateRight = animate === "both" || animate === "right";

  const hasLeft = left.length > 0;
  const hasRight = right.length > 0;

  // Connector path counts match the number of rows in each side's
  // IconCloud, so every path endpoint lands on a real tile row instead
  // of in the gap between rows. Overflow tile (when shown) counts as a
  // grid cell and can push the row total up.
  const leftVisibleCount =
    Math.min(left.length, leftFullCap) + (left.length > leftFullCap ? 1 : 0);
  const rightVisibleCount =
    Math.min(right.length, rightFullCap) +
    (right.length > rightFullCap ? 1 : 0);
  const fullPathsLeft = Math.max(1, Math.ceil(leftVisibleCount / FULL_COLUMNS));
  const fullPathsRight = Math.max(
    1,
    Math.ceil(rightVisibleCount / FULL_COLUMNS)
  );

  // Compact-side cell totals (items + optional overflow tile) drive the
  // compact connector path count the same way the full layout does. With
  // 1–2 items per side, IconCloud collapses to a single row (effective
  // columns = totalCells), so we render exactly one connector line
  // instead of two stacked.
  const leftCompactCells =
    Math.min(left.length, COMPACT_VISIBLE) +
    (left.length > COMPACT_VISIBLE ? 1 : 0);
  const rightCompactCells =
    Math.min(right.length, COMPACT_VISIBLE) +
    (right.length > COMPACT_VISIBLE ? 1 : 0);
  const compactPathsLeft = compactRowsFromCells(leftCompactCells);
  const compactPathsRight = compactRowsFromCells(rightCompactCells);

  const leftPathsCompact = computeFlowPaths({
    count: compactPathsLeft,
    side: "left",
  });
  const rightPathsCompact = computeFlowPaths({
    count: compactPathsRight,
    side: "right",
  });
  const leftPathsFull = computeFlowPaths({
    count: fullPathsLeft,
    side: "left",
  });
  const rightPathsFull = computeFlowPaths({
    count: fullPathsRight,
    side: "right",
  });

  // Compact connector matches the cloud's natural 2-row height at each
  // tile-size step (32px tiles up to @md, 40px tiles from @md onward).
  // Width is kept narrow at <@md so the whole compact stack fits inside
  // `max-w-80` (320px) without forcing the cloud's grid tracks to
  // compress — which is what produces the icon-on-connector overlap on
  // small viewports. At @md+ the wrapper grows to `max-w-md` so the
  // connector can use a more analytical 0.95×height width.
  const connectorHeightCompact = "h-[76px] w-12 @md:h-[92px] @md:w-[87px]";
  // Connector width is symmetric across both sides — sized from whichever
  // side has more rows. Keeps the cloud → hub distance equal regardless
  // of how lopsided the left vs right counts are.
  const symmetricWidthPx = rowsToConnectorWidthPx(
    size,
    Math.max(fullPathsLeft, fullPathsRight)
  );
  const connectorStyleLeftFull = {
    width: `${symmetricWidthPx}px`,
    height: `${rowsToConnectorHeightPx(size, fullPathsLeft)}px`,
  };
  const connectorStyleRightFull = {
    width: `${symmetricWidthPx}px`,
    height: `${rowsToConnectorHeightPx(size, fullPathsRight)}px`,
  };

  return (
    <div
      aria-label={ariaLabel}
      className={cn("@container w-full", className)}
      data-slot="flow-diagram"
      role="img"
      {...props}
    >
      {/* Compact layout: narrow + medium containers (default until @2xl).
       *
       * The full 3-column layout needs ~660px of container width before
       * the cloud+connector content fits without overlap — see the math
       * in CHANGELOG / commit. Below @2xl (672px) we render the compact
       * spoke instead, scaling tile/hub sizes up at @md+ so the layout
       * doesn't read as pinched between 448–672px.
       *
       * Tile/hub sizes are force-set via descendant selectors so the
       * caller's `size` prop doesn't blow out the layout at narrow
       * widths. Border radius scales with tile size to keep
       * rounded-square proportions consistent with the full layout. */}
      <div
        aria-hidden
        className={cn(
          "mx-auto flex @2xl:hidden w-full @md:max-w-md max-w-80 items-center justify-center gap-1",
          // Tile size scales up at @md to fill medium-width containers.
          "@md:[&_[data-slot=flow-tile]]:size-10 [&_[data-slot=flow-tile]]:size-8",
          "@md:[&_[data-slot=flow-tile]]:rounded-xl [&_[data-slot=flow-tile]]:rounded-lg",
          "@md:[&_[data-slot=flow-tile]>svg]:size-5 [&_[data-slot=flow-tile]>svg]:size-4",
          // Hub scales to match the larger tile cluster at @md.
          "@md:[&_[data-slot=flow-hub]]:size-16 [&_[data-slot=flow-hub]]:size-12",
          "@md:[&_[data-slot=flow-hub]_>_span:last-child]:rounded-[12px] [&_[data-slot=flow-hub]_>_span:last-child]:rounded-[8px]",
          "@md:[&_[data-slot=flow-hub]_svg]:size-7 [&_[data-slot=flow-hub]_svg]:size-6"
        )}
      >
        {hasLeft ? (
          <>
            <IconCloud
              columns={2}
              hasOverflow={left.length > COMPACT_VISIBLE}
              items={left.slice(0, COMPACT_VISIBLE)}
              overflowSize={size}
            />
            <FlowConnector
              animated={animateLeft}
              className={cn(connectorHeightCompact)}
              clusterSide="end"
              lineStyle={lineStyle}
              paths={leftPathsCompact}
              variant={animation}
            />
          </>
        ) : null}
        {center}
        {hasRight ? (
          <>
            <FlowConnector
              animated={animateRight}
              className={cn(connectorHeightCompact)}
              clusterSide="start"
              lineStyle={lineStyle}
              paths={rightPathsCompact}
              variant={animation}
            />
            <IconCloud
              columns={2}
              hasOverflow={right.length > COMPACT_VISIBLE}
              items={right.slice(0, COMPACT_VISIBLE)}
              overflowSize={size}
            />
          </>
        ) : null}
      </div>

      {/* Full layout: wide containers (≥ @2xl, 672px).
       *
       * The 3-column cloud + symmetric connector + hub needs ~660px of
       * container width to render without horizontal overlap. Below
       * @2xl we fall back to the compact layout (above) instead of
       * cramming 3 columns into a narrower space. */}
      <div
        aria-hidden
        className="@2xl:grid hidden w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center"
      >
        <div className="flex min-w-0 items-center justify-end gap-0">
          {hasLeft ? (
            <>
              <IconCloud
                columns={3}
                hasOverflow={left.length > leftFullCap}
                items={left.slice(0, leftFullCap)}
                overflowSize={size}
              />
              <FlowConnector
                animated={animateLeft}
                clusterSide="end"
                lineStyle={lineStyle}
                paths={leftPathsFull}
                style={connectorStyleLeftFull}
                variant={animation}
              />
            </>
          ) : null}
        </div>
        {center}
        <div className="flex min-w-0 items-center justify-start gap-0">
          {hasRight ? (
            <>
              <FlowConnector
                animated={animateRight}
                clusterSide="start"
                lineStyle={lineStyle}
                paths={rightPathsFull}
                style={connectorStyleRightFull}
                variant={animation}
              />
              <IconCloud
                columns={3}
                hasOverflow={right.length > rightFullCap}
                items={right.slice(0, rightFullCap)}
                overflowSize={size}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function IconCloud({
  items,
  columns,
  hasOverflow,
  overflowSize,
}: {
  items: ReactNode[];
  columns: 2 | 3;
  hasOverflow: boolean;
  overflowSize: "sm" | "md" | "lg";
}) {
  // Total cells (items + optional overflow) decide the actual rendered
  // column count — capped by the layout's max columns. Prevents empty
  // grid cells on the right edge of the cloud, which would otherwise
  // push the connector's icon-side endpoint past the actual last tile.
  const totalCells = items.length + (hasOverflow ? 1 : 0);
  const effectiveColumns = Math.max(1, Math.min(columns, totalCells));

  const columnsClass = COLUMNS_CLASS[effectiveColumns] ?? COLUMNS_CLASS[3];

  return (
    // `shrink-0` keeps the grid at its natural width even when the
    // parent flex slot is narrower than the cloud — without it, the
    // `minmax(0,1fr)` tracks collapse below tile size and tiles
    // overlap their neighbours (visible as icons stacking on top of
    // each other at small viewports).
    <div
      className={cn(
        "grid shrink-0 justify-center gap-2.5 sm:gap-3",
        columnsClass
      )}
    >
      {items.map((node, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: items are caller-provided ReactNodes; index is stable within the molecule render
        <span key={`flow-item-${index}`}>{node}</span>
      ))}
      {hasOverflow ? (
        <FlowTile size={overflowSize} variant="muted">
          <MoreHorizontal aria-hidden />
        </FlowTile>
      ) : null}
    </div>
  );
}

const COLUMNS_CLASS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
};

const TILE_PX_BY_SIZE: Record<FlowSize, number> = {
  sm: 32,
  md: 40,
  lg: 56,
};

// 12px = Tailwind `gap-3` (the breakpoint that activates inside the cloud
// at sm: and above — the common rendering case for the full layout).
const CLOUD_GAP_PX = 12;

// Mirrors IconCloud's `effectiveColumns = min(columns, totalCells)`
// collapse: with 1–2 cells, the cloud renders one row, so the connector
// renders one path.
function compactRowsFromCells(cells: number): number {
  if (cells <= 0) {
    return 1;
  }
  const effectiveColumns = Math.max(1, Math.min(COMPACT_COLUMNS, cells));
  return Math.max(1, Math.ceil(cells / effectiveColumns));
}

function rowsToConnectorHeightPx(size: FlowSize, rows: number): number {
  const tile = TILE_PX_BY_SIZE[size];
  return rows * tile + Math.max(0, rows - 1) * CLOUD_GAP_PX;
}

// Connector width is tuned to be ~95% of its height across all sizes
// and row counts so the cluster → fan-out reads with consistent curve
// geometry. Without this, wider tiles (lg) get squashed connectors and
// narrower tiles (sm) get pinched ones, both of which point the lines
// at the wrong angles.
function rowsToConnectorWidthPx(size: FlowSize, rows: number): number {
  return Math.round(rowsToConnectorHeightPx(size, rows) * 0.95);
}

export { FlowDiagram };
export type { FlowAnimateMode, FlowDiagramProps, FlowSize };
